import * as ArtifactUtils from '@wandb/weave/common/util/artifacts';
import {TargetBlank} from '@wandb/weave/common/util/links';
import {notEmpty} from '@wandb/weave/common/util/obj';
import {TailwindContents} from '@wandb/weave/components/Tailwind';
import {capitalizeFirst} from '@wandb/weave/core';
import {produce} from 'immer';
import React, {useCallback} from 'react';
import {Link} from 'react-router-dom';
import {type SortFunction} from 'react-table';
import {Grid, Header} from 'semantic-ui-react';

import {TabWaveLoader} from '../../components/RunDetails/TabWaveLoader';
import {SectionHeaderSubText} from '../../components/Section';
import WBReactTable from '../../components/WBReactTable';
import {
  type ArtifactEdge,
  type InputArtifactEdge,
  type PageInfo,
  useRunArtifactVersionsQuery,
} from '../../generated/graphql';
import {startFunctionPerformanceMeasure} from '../../util/datadog/workspace/functionPerformance';
import DOC_URLS from '../../util/doc_urls';
import {propagateErrorsContext} from '../../util/errors';
import * as Urls from '../../util/urls';
import {InstrumentedCenteredWaveLoader as Loader} from './../../components/utility/InstrumentedLoader';

const PAGE_SIZE = 20;

type ArtifactType = 'inputArtifacts' | 'outputArtifacts';
type ArtifactQueryType = ReturnType<typeof useRunArtifactVersions>['artifacts'];

type ArtifactEdgeTypes = ArtifactEdge | InputArtifactEdge;

const useRunArtifactVersions = (props: {
  entityName: string;
  projectName: string;
  runName: string;
  artifactsType: ArtifactType;
}) => {
  const {entityName, projectName, runName, artifactsType} = props;

  const queryArtifacts = useRunArtifactVersionsQuery({
    context: propagateErrorsContext(),
    variables: {
      entityName,
      projectName,
      runName,
      includeInput: artifactsType === 'inputArtifacts',
      includeOutput: artifactsType === 'outputArtifacts',
    },
    notifyOnNetworkStatusChange: true,
  });

  const edges = queryArtifacts.data?.project?.run?.[artifactsType]?.edges ?? [];
  const artifacts =
    (edges as ArtifactEdgeTypes[]).map(e => e.node).filter(notEmpty) ?? [];
  const total =
    queryArtifacts.data?.project?.run?.[artifactsType]?.totalCount ??
    artifacts.length;

  const fetchNextPage = useCallback(() => {
    const cursor =
      queryArtifacts.data?.project?.run?.[artifactsType]?.pageInfo.endCursor;
    queryArtifacts.fetchMore({
      variables: {
        after: cursor,
      },
      updateQuery: (prev, {fetchMoreResult}) => {
        const searchResult = fetchMoreResult?.project?.run?.[artifactsType];
        if (!searchResult) {
          return prev;
        }
        const endMeasure = startFunctionPerformanceMeasure(
          'immerPaginateRunArtifacts'
        );
        const result = produce(
          prev,
          (draft: {
            project: {
              run: {
                inputArtifacts: {
                  edges: ArtifactEdgeTypes[];
                  pageInfo: Pick<PageInfo, 'hasNextPage' | 'endCursor'>;
                };
                outputArtifacts: {
                  edges: ArtifactEdgeTypes[];
                  pageInfo: Pick<PageInfo, 'hasNextPage' | 'endCursor'>;
                };
              };
            };
          }) => {
            if (
              draft.project.run[artifactsType] &&
              draft.project.run[artifactsType].pageInfo.endCursor !==
                searchResult.pageInfo.endCursor
            ) {
              draft.project.run[artifactsType].edges.push(
                ...(searchResult.edges as ArtifactEdgeTypes[])
              );
              draft.project.run[artifactsType].pageInfo = searchResult.pageInfo;
            }
          }
        );
        endMeasure({
          artifactType: artifactsType,
          edgeCount: searchResult.edges.length,
        });
        return result;
      },
    });
  }, [artifactsType, queryArtifacts]);

  return {
    ...queryArtifacts,
    artifacts,
    total,
    fetchNextPage,
    hasNextPage:
      queryArtifacts.data?.project?.run?.[artifactsType]?.pageInfo
        .hasNextPage ?? false,
  };
};

interface RunArtifactsProps {
  entityName: string;
  projectName: string;
  runName: string;
  groupName?: string;
  sweepName?: string;
}

export const RunArtifactGrid: React.FC<RunArtifactsProps> = props => {
  const inputArtifacts = useRunArtifactVersions({
    ...props,
    artifactsType: 'inputArtifacts',
  });
  const outputArtifacts = useRunArtifactVersions({
    ...props,
    artifactsType: 'outputArtifacts',
  });

  const loading = inputArtifacts.loading || outputArtifacts.loading;
  const hasArtifacts =
    inputArtifacts.total !== 0 || outputArtifacts.total !== 0;

  if (loading && hasArtifacts) {
    return (
      <div style={{position: 'relative', height: 300}}>
        <Loader name="run-artifact-grid" size="huge" />
      </div>
    );
  }
  if (!hasArtifacts) {
    return null;
  }

  const hasBothArtifacts =
    inputArtifacts.total > 0 && outputArtifacts.total > 0;
  return (
    <Grid.Row columns={hasBothArtifacts ? 2 : 1}>
      {inputArtifacts.total > 0 && (
        <Grid.Column>
          <Header>Artifact Inputs</Header>
          <p className="hint-text">
            This run consumed these artifacts as inputs.{' '}
            <TargetBlank href={DOC_URLS.artifacts}>Learn more</TargetBlank>
          </p>
          <ArtifactsTable
            {...inputArtifacts}
            artifactsType="inputArtifacts"
            noHeader
          />
        </Grid.Column>
      )}
      {outputArtifacts.total > 0 && (
        <Grid.Column>
          <Header>Artifact Outputs</Header>
          <p className="hint-text">
            This run produced these artifacts as outputs. Total:{' '}
            {outputArtifacts.total}
            {outputArtifacts.total > 1000 ? ' (showing first 1000)' : ''}
            {'. '}
            <TargetBlank href={DOC_URLS.artifacts}>Learn more</TargetBlank>
          </p>
          <ArtifactsTable
            {...outputArtifacts}
            artifactsType="outputArtifacts"
            noHeader
          />
        </Grid.Column>
      )}
    </Grid.Row>
  );
};

const RunArtifacts = (props: RunArtifactsProps) => {
  const inputArtifacts = useRunArtifactVersions({
    ...props,
    artifactsType: 'inputArtifacts',
  });
  const outputArtifacts = useRunArtifactVersions({
    ...props,
    artifactsType: 'outputArtifacts',
  });

  const loaded = !inputArtifacts.loading && !outputArtifacts.loading;
  const hasOutputArtifacts =
    (outputArtifacts.data?.project?.run?.outputArtifacts?.totalCount ?? -1) > 0;
  const hasInputArtifacts =
    (inputArtifacts.data?.project?.run?.inputArtifacts?.totalCount ?? -1) > 0;
  const hasArtifacts = hasOutputArtifacts || hasInputArtifacts;
  // only show tab wave loader if we are loading the first page of artifacts
  if (!loaded && !hasArtifacts) {
    return <TabWaveLoader />;
  }
  return (
    <div style={{padding: '24px 16px'}}>
      {!hasArtifacts && loaded ? (
        <p>No artifacts are used in this run.</p>
      ) : (
        <>
          <ArtifactsTable
            {...inputArtifacts}
            artifactsType="inputArtifacts"
            hasNextPage={inputArtifacts.hasNextPage}
            fetchNextPage={inputArtifacts.fetchNextPage}
          />
          <ArtifactsTable
            {...outputArtifacts}
            artifactsType="outputArtifacts"
            hasNextPage={outputArtifacts.hasNextPage}
            fetchNextPage={outputArtifacts.fetchNextPage}
          />
        </>
      )}
    </div>
  );
};

export default RunArtifacts;

interface ArtifactsTableProps {
  children?: React.ReactNode;
  total: number;
  artifacts: ArtifactQueryType;
  artifactsType: ArtifactType;
  loading: boolean;
  noHeader?: boolean;
  hasNextPage: boolean;
  fetchNextPage: () => void;
}

const ArtifactsTable: React.FC<ArtifactsTableProps> = React.memo(props => {
  // We just have 2 types in the table, string and numbers
  const defaultSortMethod: SortFunction = useCallback((a, b) => {
    // check compare type, and set values accordingly
    let aVal = a;
    let bVal = b;
    if (typeof a.props.children === 'number') {
      aVal = a.props.children;
      bVal = b.props.children;
    } else {
      // No fields in this table is null, this is just in case check
      if (a.props.children != null && b.props.children != null) {
        aVal = (a.props.children as string).toLowerCase();
        bVal = (b.props.children as string).toLowerCase();
      }
    }

    // perform sort
    if (aVal > bVal) {
      return -1;
    }
    if (bVal > aVal) {
      return 1;
    }
    return 0;
  }, []);

  const {
    loading,
    total,
    artifacts,
    artifactsType,
    noHeader = false,
    hasNextPage,
    fetchNextPage,
  } = props;

  if (loading && total === 0) {
    return (
      <TailwindContents>
        <div className="relative h-[300px]">
          <Loader name="artifacts-table" size="huge" />
        </div>
      </TailwindContents>
    );
  }

  if (total === 0) {
    return null;
  }

  const columns = [
    {Header: 'Type', accessor: 'type', width: 180},
    {Header: 'Name', accessor: 'name'},
    {Header: 'Consumer count', accessor: 'count', width: 240},
  ];

  const rows = artifacts.map(a => {
    const {usedBy} = a;
    const displayAlias = ArtifactUtils.artifactNiceName(a);
    const url = Urls.artifact({
      entityName: a.artifactType.project.entity.name,
      projectName: a.artifactType.project.name,
      artifactTypeName: a.artifactType.name,
      artifactCollectionName: a.artifactSequence.name,
      artifactCommitHash: `v${a.versionIndex}`,
    });

    return {
      searchString: `${displayAlias}${a.artifactType.name}`,
      row: {
        type: <Link to={url}>{a.artifactType.name}</Link>,
        name: <Link to={url}>{displayAlias}</Link>,
        count: <Link to={url}>{usedBy.totalCount}</Link>,
      },
    };
  });

  return (
    <>
      {!noHeader && (
        <Header as="h2">
          {capitalizeFirst(artifactsType.replace('Artifacts', ''))} artifacts
          <br />
          <SectionHeaderSubText>
            Total artifacts: {total}
            {total > 1000
              ? '. Table search is limited to the loaded artifacts.'
              : ''}
          </SectionHeaderSubText>
        </Header>
      )}
      <WBReactTable
        className="run-artifacts-table"
        pageSize={PAGE_SIZE}
        data={rows}
        columns={columns}
        sortable={true}
        defaultSortMethod={defaultSortMethod}
        hasNextPage={hasNextPage}
        fetchingNextPage={loading}
        onFetchNextPage={fetchNextPage}
      />
    </>
  );
});
